import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from 'react-hook-form';
import { createSpecialLeaveFormSchema, type CreateSpecialLeaveFormValues } from '@/schemas/absence';
import { absenceService } from '@/api/domains/absence/absence.service';
import { ABSENCE_SCOPE_LIST_REFRESH_EVENT } from '@/api/domains/absence/absence.types';
import type {
  LC_SpecialLeaveTypeItemOut,
  LC_AbsenceWorkdayTypeItemOut,
  LC_EmployeeForAbsenceItemOut,
} from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useDebounce } from '@/hooks/ui/useDebounce';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { formatDateToYYYYMMDD, injectValidationErrors } from '@/utils';


const PAGE_SIZE = 20;
const CONFLICT_DATE_OVERLAP_CODE = 'CONFLICT_DATE_OVERLAP';

export type PermitType = '' | 'special' | 'administrative' | 'fraction';

export interface UseCreateSpecialLeaveResult {
  // Form
  form: {
    register: UseFormRegister<CreateSpecialLeaveFormValues>;
    setValue: UseFormSetValue<CreateSpecialLeaveFormValues>;
    watch: UseFormWatch<CreateSpecialLeaveFormValues>;
    errors: FieldErrors<CreateSpecialLeaveFormValues>;
    trigger: UseFormTrigger<CreateSpecialLeaveFormValues>;
  };
  isSubmitted: boolean;
  isSubmitting: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;

  // Employee selector
  employeeOptions: SelectorOption[];
  employeeBlockedIds: Set<number>;
  isLoadingEmployees: boolean;
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  handleEmployeeSearch: (search: string) => void;
  handleEmployeeChange: (value: string | string[]) => void;
  loadMoreEmployees: () => Promise<void>;

  // Permit subtype selector (Permiso especial / administrativo / fracción)
  permitType: PermitType;
  permitTypeOptions: SelectorOption[];
  handlePermitTypeChange: (value: string | string[]) => void;

  // Special leave types
  specialLeaveTypeOptions: SelectorOption[];
  isLoadingSpecialLeaveTypes: boolean;

  // Workday types
  workdayTypeOptions: SelectorOption[];
  isLoadingWorkdayTypes: boolean;
}

export function useCreateSpecialLeave(): UseCreateSpecialLeaveResult {
  const { t: translate } = useTranslation('absence');
  const { showApiError, showSuccess, showNotification, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  // ── Permit subtype selector ─────────────────────────────────────────────────
  // Solo "Permiso especial" está implementado; administrativo y fracción quedan
  // deshabilitados en el combo hasta que sus tickets futuros los habiliten.
  const [permitType, setPermitType] = useState<PermitType>('');

  const permitTypeOptions = useMemo<SelectorOption[]>(
    () => [
      { value: 'special', label: translate('specialLeave.modal.options.special.title') },
      {
        value: 'administrative',
        label: translate('specialLeave.modal.options.administrative.title'),
        disabled: true,
      },
      {
        value: 'fraction',
        label: translate('specialLeave.modal.options.fraction.title'),
        disabled: true,
      },
    ],
    [translate],
  );

  const handlePermitTypeChange = useCallback((value: string | string[]) => {
    const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
    setPermitType(stringValue as PermitType);
  }, []);

  // ── Special leave types ─────────────────────────────────────────────────────
  // Nota: no se usa AbortController aquí — es un catálogo pequeño de una sola pasada;
  // abortarlo en el cleanup generaría TaskCanceledException en el backend durante los
  // doble-montajes de React.StrictMode en desarrollo (ver useCatalogLoader.ts).
  const [specialLeaveTypes, setSpecialLeaveTypes] = useState<LC_SpecialLeaveTypeItemOut[]>([]);
  const [isLoadingSpecialLeaveTypes, setIsLoadingSpecialLeaveTypes] = useState(false);

  const loadSpecialLeaveTypes = useCallback(async () => {
    setIsLoadingSpecialLeaveTypes(true);
    try {
      const response = await absenceService.getSpecialLeaveTypes();
      if (response.success && response.data) {
        setSpecialLeaveTypes(response.data ?? []);
      } else {
        showApiError(response, translate('specialLeave.errors.loadSpecialLeaveTypesFailed'));
      }
    } finally {
      setIsLoadingSpecialLeaveTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadSpecialLeaveTypes();
  }, [loadSpecialLeaveTypes]);

  const specialLeaveTypeOptions = useMemo<SelectorOption[]>(
    () => specialLeaveTypes.map((type) => ({ value: String(type.id), label: type.name })),
    [specialLeaveTypes],
  );

  // ── Workday types ──────────────────────────────────────────────────────────
  // Mismo motivo que loadSpecialLeaveTypes: catálogo estático, sin AbortController.
  const [workdayTypes, setWorkdayTypes] = useState<LC_AbsenceWorkdayTypeItemOut[]>([]);
  const [isLoadingWorkdayTypes, setIsLoadingWorkdayTypes] = useState(false);

  const loadWorkdayTypes = useCallback(async () => {
    setIsLoadingWorkdayTypes(true);
    try {
      const response = await absenceService.getAbsenceWorkdayTypes();
      if (response.success && response.data) {
        setWorkdayTypes(response.data ?? []);
      } else {
        showApiError(response, translate('specialLeave.errors.loadWorkdayTypesFailed'));
      }
    } finally {
      setIsLoadingWorkdayTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadWorkdayTypes();
  }, [loadWorkdayTypes]);

  const workdayTypeOptions = useMemo<SelectorOption[]>(
    () => workdayTypes.map((type) => ({ value: type.code, label: type.label })),
    [workdayTypes],
  );

  // ── Employee selector ──────────────────────────────────────────────────────
  const [employees, setEmployees] = useState<LC_EmployeeForAbsenceItemOut[]>([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingMoreEmployees, setIsLoadingMoreEmployees] = useState(false);
  const selectedEmployeeRef = useRef<LC_EmployeeForAbsenceItemOut | null>(null);

  const loadEmployees = useCallback(
    async (page: number, search: string, append: boolean = false) => {
      if (append) {
        setIsLoadingMoreEmployees(true);
      } else {
        setIsLoadingEmployees(true);
      }
      try {
        const response = await absenceService.getEmployeesForAbsence({
          page,
          pageSize: PAGE_SIZE,
          searchText: search || undefined,
        });

        if (response.success && response.data) {
          const newItems = response.data.data ?? [];
          setEmployees((previous) => {
            let result = append ? [...previous, ...newItems] : newItems;
            const selected = selectedEmployeeRef.current;
            if (selected && !result.some((employee) => employee.id === selected.id)) {
              result = [selected, ...result];
            }
            return result;
          });
          setEmployeeHasMore(response.data.pagination.hasNextPage);
          setEmployeePage(page);
        } else {
          showApiError(response, translate('specialLeave.errors.loadEmployeesFailed'));
        }
      } finally {
        if (append) {
          setIsLoadingMoreEmployees(false);
        } else {
          setIsLoadingEmployees(false);
        }
      }
    },
    [showApiError, translate],
  );

  useEffect(() => {
    void loadEmployees(1, '');
  }, [loadEmployees]);

  const loadMoreEmployees = useCallback(async () => {
    if (!isLoadingMoreEmployees && employeeHasMore) {
      await loadEmployees(employeePage + 1, employeeSearchText, true);
    }
  }, [isLoadingMoreEmployees, employeeHasMore, employeePage, employeeSearchText, loadEmployees]);

  const handleEmployeeSearch = useCallback(
    (search: string) => {
      setEmployeeSearchText(search);
      void loadEmployees(1, search);
    },
    [loadEmployees],
  );

  const employeeOptions = useMemo<SelectorOption[]>(
    () =>
      employees.map((employee) => ({
        value: String(employee.id),
        label: employee.fullName
          ? `${employee.fullName}${employee.employeeTaxIdentificationNumber ? ` (${employee.employeeTaxIdentificationNumber})` : ''}`
          : String(employee.id),
        supportingText: employee.companyName || undefined,
        disabled: employee.isBlocked,
        disabledTooltip: employee.isBlocked
          ? translate('specialLeave.form.blockedEmployeeTooltip')
          : undefined,
      })),
    [employees, translate],
  );

  const employeeBlockedIds = useMemo<Set<number>>(
    () => new Set(
      employees
        .filter((employee) => employee.isBlocked)
        .map((employee) => employee.id),
    ),
    [employees],
  );

  // ── Loading state ──────────────────────────────────────────────────────────
  const initialLoadDoneRef = useRef(false);
  const areCatalogsLoaded = useMemo(
    () => !isLoadingEmployees && !isLoadingSpecialLeaveTypes && !isLoadingWorkdayTypes,
    [isLoadingEmployees, isLoadingSpecialLeaveTypes, isLoadingWorkdayTypes],
  );

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      setLoading(!areCatalogsLoaded);
      if (areCatalogsLoaded) {
        initialLoadDoneRef.current = true;
      }
    }
  }, [areCatalogsLoaded, setLoading]);

  // ── Form ───────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlapErrorBaselineRef = useRef<{ startDate: string; endDate: string } | null>(null);

  const {
    register,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState,
  } = useForm<CreateSpecialLeaveFormValues>({
    resolver: zodResolver(createSpecialLeaveFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      employeeTargetId: null,
      specialLeaveTypeId: null,
      startDate: '',
      endDate: '',
      workdayType: undefined as unknown as string,
      dayCount: null,
      returnDate: '',
      detail: '',
    },
  });

  // Fecha de término = Fecha de inicio + Cantidad de días, recalculada automáticamente.
  const startDateForEndDate = watch('startDate');
  const dayCountForEndDate = watch('dayCount');

  useEffect(() => {
    const dayCount = Number(dayCountForEndDate);
    if (!startDateForEndDate || !Number.isInteger(dayCount) || dayCount <= 0) return;

    const [year, month, day] = startDateForEndDate.split('-').map(Number);
    const computedEndDate = new Date(year, month - 1, day);
    computedEndDate.setDate(computedEndDate.getDate() + dayCount - 1);

    setValue('endDate', formatDateToYYYYMMDD(computedEndDate), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [startDateForEndDate, dayCountForEndDate, setValue]);

  // Fecha de retorno = próximo día hábil posterior a Fecha de término, calculada por el backend.
  const returnDateRequestIdRef = useRef(0);

  const calculateReturnDate = useCallback(
    async (employeeTargetId: number, endDate: string, workdayType: string) => {
      const requestId = ++returnDateRequestIdRef.current;
      const response = await absenceService.calculateReturnDate({
        employeeTargetId,
        endDate,
        workdayType,
      });
      if (requestId !== returnDateRequestIdRef.current) return; // respuesta obsoleta, ignorar
      if (response.success && response.data) {
        setValue('returnDate', response.data.returnDate, { shouldDirty: true });
      } else {
        showApiError(response, translate('specialLeave.errors.calculateReturnDateFailed'));
      }
    },
    [setValue, showApiError, translate],
  );

  const employeeTargetIdForReturnDate = watch('employeeTargetId');
  const endDateForReturnDate = watch('endDate');
  const workdayTypeForReturnDate = watch('workdayType');
  const debouncedEndDateForReturnDate = useDebounce(endDateForReturnDate, 400);

  useEffect(() => {
    if (
      !employeeTargetIdForReturnDate ||
      !debouncedEndDateForReturnDate ||
      !workdayTypeForReturnDate
    ) {
      setValue('returnDate', '');
      return;
    }
    void calculateReturnDate(
      employeeTargetIdForReturnDate as number,
      debouncedEndDateForReturnDate,
      workdayTypeForReturnDate,
    );
  }, [
    employeeTargetIdForReturnDate,
    debouncedEndDateForReturnDate,
    workdayTypeForReturnDate,
    calculateReturnDate,
    setValue,
  ]);

  // Si el usuario cambia las fechas tras un error de solapamiento, se limpia para evitar confusión.
  useEffect(() => {
    const baseline = overlapErrorBaselineRef.current;
    if (!baseline) return;
    if (baseline.startDate !== startDateForEndDate || baseline.endDate !== endDateForReturnDate) {
      overlapErrorBaselineRef.current = null;
      clearErrors(['startDate', 'endDate']);
    }
  }, [startDateForEndDate, endDateForReturnDate, clearErrors]);

  const onValid = useCallback(
    async (data: CreateSpecialLeaveFormValues) => {
      setIsSubmitting(true);
      try {
        const response = await absenceService.createSpecialLeave({
          employeeTargetId: data.employeeTargetId as number,
          specialLeaveTypeId: data.specialLeaveTypeId as number,
          startDate: data.startDate,
          workdayType: data.workdayType,
          dayCount: data.dayCount as number,
          detail: data.detail || undefined,
        });

        if (response.success) {
          showSuccess(translate('specialLeave.messages.createSuccess'));
          window.dispatchEvent(new CustomEvent(ABSENCE_SCOPE_LIST_REFRESH_EVENT));
          closeSidePanel?.();
        } else if (response.error?.code === CONFLICT_DATE_OVERLAP_CODE) {
          overlapErrorBaselineRef.current = { startDate: data.startDate, endDate: data.endDate };
          setError('startDate', { message: 'absence:specialLeave.errors.dateOverlap' });
          setError('endDate', { message: 'absence:specialLeave.errors.dateOverlap' });
          showNotification('error', translate('specialLeave.errors.dateOverlap'));
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('specialLeave.errors.createFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [closeSidePanel, setError, showApiError, showNotification, showSuccess, translate],
  );

  const handleEmployeeChange = useCallback(
    (value: string | string[]) => {
      const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
      const numericId = normalizeNumericSelectValue(stringValue);
      if (numericId !== undefined) {
        setValue('employeeTargetId', numericId as number, { shouldDirty: true });
        const found = employees.find((employee) => employee.id === numericId);
        if (found) {
          selectedEmployeeRef.current = found;
        }
      }
    },
    [employees, setValue],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      const wrappedValid = (data: CreateSpecialLeaveFormValues) => { void onValid(data); };
      const wrappedInvalid = () => {};
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid],
  );

  return {
    form: {
      register,
      setValue,
      watch,
      errors: formState.errors,
      trigger,
    },
    isSubmitted: formState.isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    employeeBlockedIds,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    permitType,
    permitTypeOptions,
    handlePermitTypeChange,
    specialLeaveTypeOptions,
    isLoadingSpecialLeaveTypes,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
  };
}

export default useCreateSpecialLeave;
